import api from "./index";
import SecureSS from "../utils/SecureStorage";

/**
 * Validate if user has permission to access wallet features
 * User must have userType = "CLIENT" and role = "CLIENT_ADMIN", "CLIENT_FINANCE", or "CLIENT_COMMERCIAL_TEAM"
 * @returns {Object} { isValid: boolean, clientId: number, error: string, shouldLogout: boolean }
 */
const validateUserPermissions = () => {
  const userProfile = SecureSS.getUserProfile();

  if (!userProfile) {
    return {
      isValid: false,
      error: "User not authenticated",
      shouldLogout: true,
    };
  }

  const { userType, role, clientId } = userProfile;

  // Validate userType - if not CLIENT, user is unauthorized to access this app
  if (userType !== "CLIENT") {
    return {
      isValid: false,
      error: "Unauthorized to access",
      shouldLogout: true,
    };
  }

  // Validate role - if CLIENT but wrong role, not authorized to use the app
  const allowedRoles = [
    "CLIENT_ADMIN",
    "CLIENT_FINANCE",
    "CLIENT_COMMERCIAL_TEAM",
  ];
  if (!allowedRoles.includes(role)) {
    return {
      isValid: false,
      error: "Not authorized to use the app",
      shouldLogout: true,
    };
  }

  // Validate clientId exists
  if (!clientId) {
    return {
      isValid: false,
      error: "Client ID not found in user profile.",
      shouldLogout: true,
    };
  }

  return { isValid: true, clientId };
};

/**
 * Update currency thresholds for the current client
 * Calls ht-admin which wraps wallet-service /api/v1/account/threshold/{clientId}
 * @param {Object} currencyThresholds - Map of currency codes to threshold values
 * @example
 * updateThresholds({
 *   USD: 5000.00,
 *   EUR: 4500.00,
 *   GBP: 4000.00,
 *   default: 3000.00
 * })
 * @returns {Promise} Response with updated currencyThresholds map
 */
export const updateThresholds = async (currencyThresholds) => {
  // Validate user permissions and get clientId
  const validation = validateUserPermissions();
  if (!validation.isValid) {
    throw new Error(validation.error);
  }

  // Validate input
  if (!currencyThresholds || typeof currencyThresholds !== 'object') {
    throw new Error("Currency thresholds must be a valid object");
  }

  // Validate threshold values are non-negative numbers
  for (const [currency, value] of Object.entries(currencyThresholds)) {
    if (typeof value !== 'number' || value < 0) {
      throw new Error(`Invalid threshold value for ${currency}: must be a non-negative number`);
    }
  }

  return api.putRequest(`/1.0/wallet/threshold/${validation.clientId}`, {
    currencyThresholds,
  });
};
